import { Injectable, Injector, Provider } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GapDeniedState } from '../guards/gap-permission.guard';
import { GapAccessService } from '../services/gap-access/gap-access.service';
import { asGapError, getAuthzRefusal } from '../util/authz-refusal';

/**
 * What APIM's authz check refuses, the UI says:
 * - no access to GAP at all: every call would be refused, so the user goes to the access page;
 * - too low a level for this call (`requires admin`): the user stays, and the call fails with the level it needs, worded
 *   as the GAP services word an error (`{ Description }`), so that the page shows it as it shows any other.
 * Either way the user's level is asked again: it changed since the page offered the call.
 */
@Injectable()
export class AccessDeniedInterceptor implements HttpInterceptor {
  constructor(
    private readonly _router: Router,
    // GapAccessService calls authz through HttpClient, which runs this interceptor: taken when needed, not to build it
    private readonly _injector: Injector
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError(error => {
        const refusal = getAuthzRefusal(error);
        if (!refusal) {
          return throwError(() => error);
        }
        this._injector.get(GapAccessService).refresh().subscribe();
        // A navigation running is its guard's to send there, to the page it was opening
        if (refusal.noAccess && !this._router.url.startsWith('/error') && !this._router.getCurrentNavigation()) {
          const denied: GapDeniedState = { reason: 'no-access', returnUrl: this._router.url };
          this._router.navigate(['/error'], { state: denied, replaceUrl: true });
        }
        return throwError(() => asGapError(error, refusal));
      })
    );
  }
}

export const accessDeniedInterceptorProvider: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AccessDeniedInterceptor,
  multi: true
};
